/**
 * useBudgetForm Hook
 * Hook for managing budget forms with React Hook Form integration and validations
 */

import { useCallback, useEffect, useState } from 'react';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { BudgetFormData, BudgetItem, BudgetValidationResult, UseBudgetFormReturn } from '@/types/budget';
import { useClients } from '@/hooks/useClients';
import { useCategories } from '@/hooks/useCategories';
import { useCostCenters } from '@/hooks/useCostCenters';

// Validation schemas
const budgetItemSchema = z.object({
  description: z.string().min(1, 'Descrição é obrigatória'),
  quantity: z.number().min(0.01, 'Quantidade deve ser maior que zero'),
  unit_value: z.number().min(0, 'Valor unitário não pode ser negativo'),
  item_type: z.enum(['service', 'product']),
  service_id: z.string().optional(),
  product_id: z.string().optional(),
  cost_center_id: z.string().optional(),
  category_id: z.string().optional(),
  notes: z.string().optional()
}).refine(
  (data) => {
    if (data.item_type === 'service') {
      return !!data.service_id;
    }
    if (data.item_type === 'product') {
      return !!data.product_id;
    }
    return true;
  },
  {
    message: 'Selecione um serviço ou produto',
    path: ['service_id']
  }
);

const budgetFormSchema = z.object({
  client_id: z.string().min(1, 'Cliente é obrigatório'),
  title: z.string().optional(),
  description: z.string().optional(),
  items: z.array(budgetItemSchema).min(1, 'Adicione pelo menos um item'),
  discount_percentage: z.number().min(0).max(100).optional(),
  discount_amount: z.number().min(0).optional(),
  priority: z.enum(['low', 'medium', 'high', 'urgent']).optional(),
  validity_type: z.enum(['days', 'date', 'unlimited']),
  validity_days: z.number().min(1).optional(),
  validity_date: z.string().optional(),
  responsible_user_id: z.string().optional(),
  notes: z.string().optional(),
  terms: z.string().optional(),
  payment_terms: z.string().optional(),
  delivery_terms: z.string().optional()
}).refine(
  (data) => {
    if (data.validity_type === 'days') {
      return !!data.validity_days;
    }
    if (data.validity_type === 'date') {
      return !!data.validity_date;
    }
    return true;
  },
  {
    message: 'Preencha a validade conforme o tipo selecionado',
    path: ['validity_days']
  }
).refine(
  (data) => {
    // Either discount percentage or amount, not both
    return !(data.discount_percentage && data.discount_amount);
  },
  {
    message: 'Use percentual ou valor de desconto, não ambos',
    path: ['discount_amount']
  }
);

interface UseBudgetFormOptions {
  defaultValues?: Partial<BudgetFormData>;
  mode?: 'onSubmit' | 'onBlur' | 'onChange';
  reValidateMode?: 'onChange' | 'onBlur' | 'onSubmit';
  shouldUnregister?: boolean;
}

const defaultFormValues: BudgetFormData = {
  client_id: '',
  title: '',
  description: '',
  items: [],
  discount_percentage: 0,
  discount_amount: 0,
  priority: 'medium',
  validity_type: 'days',
  validity_days: 7,
  validity_date: '',
  responsible_user_id: '',
  notes: '',
  terms: '',
  payment_terms: '',
  delivery_terms: ''
};

export function useBudgetForm(options: UseBudgetFormOptions = {}): UseBudgetFormReturn {
  const {
    defaultValues = {},
    mode = 'onSubmit',
    reValidateMode = 'onChange',
    shouldUnregister = false
  } = options;

  const [isCalculating, setIsCalculating] = useState(false);

  // External data
  const { clients } = useClients();
  const { categories } = useCategories({ type: 'income' });
  const { costCenters } = useCostCenters();

  // Form initialization
  const form = useForm<BudgetFormData>({
    resolver: zodResolver(budgetFormSchema),
    defaultValues: {
      ...defaultFormValues,
      ...defaultValues
    },
    mode,
    reValidateMode,
    shouldUnregister
  });

  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    reset,
    watch,
    formState: {
      errors,
      touchedFields,
      isValid,
      isDirty,
      isSubmitting
    }
  } = form;

  // Field array for items
  const {
    fields: itemFields,
    append: appendItem,
    remove: removeItem,
    update: updateItem
  } = useFieldArray({
    control,
    name: 'items'
  });

  // Watch for changes in discount fields to auto-calculate
  const discountPercentage = watch('discount_percentage');
  const discountAmount = watch('discount_amount');
  const items = watch('items');

  // Auto-calculate totals when items or discounts change
  useEffect(() => {
    if (items && items.length > 0) {
      calculateTotals();
    }
  }, [items, discountPercentage, discountAmount]);

  // Calculate totals
  const calculateTotals = useCallback(() => {
    setIsCalculating(true);

    try {
      const currentItems = getValues('items');
      const currentDiscountPercentage = getValues('discount_percentage') || 0;
      const currentDiscountAmount = getValues('discount_amount') || 0;

      const subtotal = currentItems.reduce((sum, item) => {
        const quantity = item.quantity || 0;
        const unitValue = item.unit_value || 0;
        return sum + (quantity * unitValue);
      }, 0);

      let discount = currentDiscountAmount;
      if (currentDiscountPercentage > 0 && !currentDiscountAmount) {
        discount = subtotal * (currentDiscountPercentage / 100);
      }

      const discountedSubtotal = Math.max(0, subtotal - discount);

      // Tax calculation (can be made configurable)
      const taxRate = 0; // 0% tax for now
      const taxAmount = discountedSubtotal * taxRate;
      const finalValue = discountedSubtotal + taxAmount;

      // Update item totals
      currentItems.forEach((item, index) => {
        const itemTotal = (item.quantity || 0) * (item.unit_value || 0);
        setValue(`items.${index}.total_value`, itemTotal);
      });

      setIsCalculating(false);

      return {
        subtotal,
        discountAmount: discount,
        taxAmount,
        finalValue
      };
    } catch (error) {
      console.error('Error calculating totals:', error);
      setIsCalculating(false);
      return {
        subtotal: 0,
        discountAmount: 0,
        taxAmount: 0,
        finalValue: 0
      };
    }
  }, [getValues, setValue]);

  // Add new item
  const addItem = useCallback((item: Omit<BudgetItem, 'id' | 'budget_id' | 'created_at' | 'updated_at'>) => {
    const newItem = {
      description: item.description || '',
      quantity: item.quantity || 1,
      unit_value: item.unit_value || 0,
      total_value: (item.quantity || 1) * (item.unit_value || 0),
      item_type: item.item_type || 'service',
      service_id: item.service_id || '',
      product_id: item.product_id || '',
      cost_center_id: item.cost_center_id || '',
      category_id: item.category_id || '',
      notes: item.notes || ''
    };

    appendItem(newItem);

    // Trigger calculation
    setTimeout(() => calculateTotals(), 0);
  }, [appendItem, calculateTotals]);

  // Remove item
  const removeItemAtIndex = useCallback((index: number) => {
    removeItem(index);

    // Trigger calculation
    setTimeout(() => calculateTotals(), 0);
  }, [removeItem, calculateTotals]);

  // Update item
  const updateItemAtIndex = useCallback((index: number, item: Partial<BudgetItem>) => {
    updateItem(index, item);

    // Trigger calculation
    setTimeout(() => calculateTotals(), 0);
  }, [updateItem, calculateTotals]);

  // Validate form
  const validate = useCallback((): BudgetValidationResult => {
    const currentValues = getValues();
    const result = budgetFormSchema.safeParse(currentValues);

    if (result.success) {
      return {
        isValid: true,
        errors: [],
        warnings: []
      };
    } else {
      const errors = result.error.errors.map(err => `${err.path.join('.')}: ${err.message}`);

      // Add business logic warnings
      const warnings: string[] = [];

      if (currentValues.items.length === 0) {
        errors.push('items: Adicione pelo menos um item ao orçamento');
      }

      const totalValue = currentValues.items.reduce((sum, item) =>
        sum + ((item.quantity || 0) * (item.unit_value || 0)), 0
      );

      if (totalValue === 0) {
        warnings.push('O valor total do orçamento é zero');
      }

      if (totalValue > 1000000) {
        warnings.push('Orçamento com valor muito alto, revise os valores');
      }

      if (!currentValues.client_id) {
        errors.push('client_id: Selecione um cliente');
      }

      return {
        isValid: false,
        errors,
        warnings
      };
    }
  }, [getValues]);

  // Auto-select service/product when type changes
  const handleItemTypeChange = useCallback((index: number, itemType: 'service' | 'product') => {
    setValue(`items.${index}.item_type`, itemType);

    // Clear related fields
    setValue(`items.${index}.service_id`, '');
    setValue(`items.${index}.product_id`, '');

    // Set default values based on type
    if (itemType === 'service') {
      setValue(`items.${index}.quantity`, 1);
    } else {
      setValue(`items.${index}.quantity`, 1);
    }
  }, [setValue]);

  // Handle service/product selection
  const handleServiceSelect = useCallback((index: number, serviceId: string) => {
    // Find service details (this would come from a services hook)
    // For now, just set the ID
    setValue(`items.${index}.service_id`, serviceId);
    setValue(`items.${index}.product_id`, '');
  }, [setValue]);

  const handleProductSelect = useCallback((index: number, productId: string) => {
    // Find product details (this would come from a products hook)
    // For now, just set the ID
    setValue(`items.${index}.product_id`, productId);
    setValue(`items.${index}.service_id`, '');
  }, [setValue]);

  // Reset form with new values
  const resetForm = useCallback((newValues?: Partial<BudgetFormData>) => {
    const valuesToReset = newValues ? {
      ...defaultFormValues,
      ...newValues
    } : defaultFormValues;

    reset(valuesToReset);
  }, [reset]);

  // Get available clients for select
  const getClientOptions = useCallback(() => {
    return clients?.map(client => ({
      value: client.id,
      label: client.name,
      document: client.document
    })) || [];
  }, [clients]);

  // Get available categories for select
  const getCategoryOptions = useCallback(() => {
    return categories?.map(category => ({
      value: category.id,
      label: category.name
    })) || [];
  }, [categories]);

  // Get available cost centers for select
  const getCostCenterOptions = useCallback(() => {
    return costCenters?.map(center => ({
      value: center.id,
      label: center.name
    })) || [];
  }, [costCenters]);

  // Computed values
  const values = getValues();
  const errorsObject = errors as Record<string, any>;
  const touchedObject = touchedFields as Record<string, boolean>;

  return {
    form,
    values,
    errors: errorsObject,
    touched: touchedObject,
    isValid,
    isDirty,
    isSubmitting,
    handleSubmit,
    setValue,
    getValues,
    reset: resetForm,
    addItem,
    removeItem: removeItemAtIndex,
    updateItem: updateItemAtIndex,
    calculateTotals,
    validate,

    // Additional helpers
    itemFields,
    control,
    watch,
    handleItemTypeChange,
    handleServiceSelect,
    handleProductSelect,
    getClientOptions,
    getCategoryOptions,
    getCostCenterOptions,

    // States
    isCalculating
  };
}

// Helper hook for creating budget from template
export function useBudgetFormFromTemplate(templateId?: string) {
  const form = useBudgetForm();

  useEffect(() => {
    if (templateId) {
      // Load template data and populate form
      // This would integrate with a templates hook
      console.log('Loading template:', templateId);
    }
  }, [templateId]);

  return form;
}

// Helper hook for editing existing budget
export function useBudgetFormFromBudget(budgetId?: string) {
  const form = useBudgetForm();

  useEffect(() => {
    if (budgetId) {
      // Load budget data and populate form
      // This would integrate with useBudgetById hook
      console.log('Loading budget:', budgetId);
    }
  }, [budgetId]);

  return form;
}